import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import imageCompression from "browser-image-compression";
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Image as ImageIcon,
  UploadSimple,
  Star,
  Trash,
  ArrowsOutCardinal,
} from "@phosphor-icons/react";

// ─── Constraints ────────────────────────────────────────────────────────
const MAX_IMAGES = 8;
const COMPRESS_OPTS = {
  maxSizeMB: 0.5,          // 500KB per image (hard budget vs 16MB Mongo doc)
  maxWidthOrHeight: 1600,
  useWebWorker: true,
  fileType: "image/jpeg",
  initialQuality: 0.8,
};

// Approx bytes from base64 string length
const b64Size = (s) => Math.floor(((String(s || "").length - "data:image/jpeg;base64,".length) * 3) / 4);
const totalSize = (arr) => (arr || []).reduce((acc, s) => acc + b64Size(s), 0);
const fmtMB = (bytes) => `${(bytes / 1024 / 1024).toFixed(2)}MB`;

// ─── Single sortable slot ───────────────────────────────────────────────
function Slot({ id, index, src, onSetMain, onRemove, onPick }) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
    zIndex: isDragging ? 5 : 0,
  };

  const isMain = index === 0;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative aspect-square rounded-xl overflow-hidden border-2 group ${
        src
          ? isMain
            ? "border-primary shadow-[0_0_0_2px_rgba(201,169,97,0.35)]"
            : "border-primary/25"
          : "border-dashed border-primary/30 bg-[#0B1A3A]"
      }`}
      data-testid={`car-slot-${index}`}
    >
      {src ? (
        <>
          <img src={src} alt={`slot-${index}`} className="w-full h-full object-cover" />

          {/* main badge */}
          {isMain && (
            <div className="absolute top-1.5 right-1.5 inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary text-primary-foreground">
              <Star size={10} weight="fill" /> رئيسية
            </div>
          )}

          {/* drag handle */}
          <button
            type="button"
            {...attributes}
            {...listeners}
            className="absolute top-1.5 left-1.5 w-7 h-7 rounded-full bg-black/55 text-white opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center justify-center cursor-grab active:cursor-grabbing touch-none"
            aria-label="اسحب لإعادة الترتيب"
            data-testid={`car-slot-${index}-drag`}
          >
            <ArrowsOutCardinal size={13} weight="bold" />
          </button>

          {/* bottom actions */}
          <div className="absolute inset-x-0 bottom-0 p-1.5 flex items-center gap-1 bg-gradient-to-t from-black/85 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
            {!isMain && (
              <button
                type="button"
                onClick={() => onSetMain(index)}
                className="flex-1 text-[10px] font-bold px-2 py-1 rounded-md bg-primary/90 text-primary-foreground hover:bg-primary inline-flex items-center justify-center gap-1"
                data-testid={`car-slot-${index}-set-main`}
              >
                <Star size={10} weight="fill" /> تعيين رئيسية
              </button>
            )}
            <button
              type="button"
              onClick={() => onRemove(index)}
              className="w-7 h-7 rounded-md bg-destructive/90 hover:bg-destructive text-white inline-flex items-center justify-center"
              aria-label="حذف"
              data-testid={`car-slot-${index}-remove`}
            >
              <Trash size={11} weight="bold" />
            </button>
          </div>
        </>
      ) : (
        <button
          type="button"
          onClick={() => onPick(index)}
          className="w-full h-full flex flex-col items-center justify-center gap-1 text-primary/50 hover:text-primary hover:bg-primary/5 transition-colors"
          data-testid={`car-slot-${index}-add`}
        >
          <UploadSimple size={22} weight="bold" />
          <span className="text-[10px] font-bold">صورة {index + 1}</span>
        </button>
      )}
    </div>
  );
}

// ─── Grid editor ────────────────────────────────────────────────────────
export default function CarImagesGrid({ value = [], onChange, onBusyChange }) {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const nextIndexRef = useRef(null);

  const setBusyState = (v) => {
    setBusy(v);
    onBusyChange?.(v);
  };

  // Fill up to 8 slots.
  const filled = (value || []).slice(0, MAX_IMAGES);
  const emptyCount = Math.max(0, MAX_IMAGES - filled.length);
  const slots = [
    ...filled.map((src, i) => ({ id: `img-${i}-${(src || "").slice(-16)}`, src, index: i })),
    ...Array.from({ length: emptyCount }, (_, i) => ({
      id: `empty-${filled.length + i}`,
      src: null,
      index: filled.length + i,
    })),
  ];

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const emit = (arr) => onChange?.(arr.filter(Boolean).slice(0, MAX_IMAGES));

  const setMain = (idx) => {
    const arr = [...filled];
    if (idx <= 0 || idx >= arr.length) return;
    const [m] = arr.splice(idx, 1);
    arr.unshift(m);
    emit(arr);
    toast.success("تم تعيينها كصورة رئيسية");
  };

  const remove = (idx) => {
    const arr = [...filled];
    arr.splice(idx, 1);
    emit(arr);
  };

  const pickAt = (idx) => {
    nextIndexRef.current = idx;
    fileRef.current?.click();
  };

  const compressOne = async (file) => {
    if (file.size > 15 * 1024 * 1024) throw new Error("الحد الأقصى قبل الضغط 15MB");
    const compressed = await imageCompression(file, COMPRESS_OPTS);
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = (e) => resolve(e.target.result);
      r.onerror = reject;
      r.readAsDataURL(compressed);
    });
  };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    e.target.value = "";

    const validFiles = files.filter((f) => /^image\/(jpe?g|png|webp|heic|heif)$/.test(f.type));
    if (validFiles.length !== files.length) {
      toast.error("صور فقط (JPG/PNG/WebP)");
    }
    if (!validFiles.length) return;

    const availableSlots = MAX_IMAGES - filled.length;
    if (validFiles.length > availableSlots) {
      toast.error(`متبقّي ${availableSlots} خانات فقط`);
    }
    const toProcess = validFiles.slice(0, availableSlots);
    if (!toProcess.length) return;

    setBusyState(true);
    try {
      const compressed = [];
      for (const f of toProcess) {
        try {
          const b64 = await compressOne(f);
          compressed.push(b64);
        } catch (err) {
          toast.error(`فشل ضغط ${f.name}: ${err.message || err}`);
        }
      }
      if (!compressed.length) return;

      const merged = [...filled, ...compressed].slice(0, MAX_IMAGES);
      const size = totalSize(merged);
      if (size > 5 * 1024 * 1024) {
        toast.error(`مجموع الصور ${fmtMB(size)} > 5MB — احذف بعض الصور أو استخدم صوراً أصغر.`);
        return;
      }
      emit(merged);
      toast.success(`تم رفع ${compressed.length} صورة (${fmtMB(totalSize(compressed))})`);
    } finally {
      setBusyState(false);
      nextIndexRef.current = null;
    }
  };

  const onDragEnd = useCallback(
    (evt) => {
      const { active, over } = evt;
      if (!over || active.id === over.id) return;
      const oldIdx = slots.findIndex((s) => s.id === active.id);
      const newIdx = slots.findIndex((s) => s.id === over.id);
      if (oldIdx < 0 || newIdx < 0) return;
      // Only shuffle within filled slots
      const filledIds = slots.filter((s) => s.src).map((s) => s.id);
      if (!filledIds.includes(active.id) || !filledIds.includes(over.id)) return;
      const reordered = arrayMove(
        slots.filter((s) => s.src),
        oldIdx,
        newIdx,
      ).map((s) => s.src);
      emit(reordered);
    },
    [slots, filled], // eslint-disable-line
  );

  const total = totalSize(filled);

  return (
    <div dir="rtl">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs text-white/60 text-right">
          صور السيارة (حتى <span className="num">{MAX_IMAGES}</span> صور)
        </div>
        <div className="text-[11px] font-bold num" dir="ltr">
          <span className="text-primary">{filled.length}</span>
          <span className="text-white/40">/{MAX_IMAGES}</span>
          <span className="text-white/40 mx-1">·</span>
          <span className={total > 4 * 1024 * 1024 ? "text-destructive" : "text-white/60"}>
            {fmtMB(total)}
          </span>
        </div>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={slots.map((s) => s.id)} strategy={rectSortingStrategy}>
          <div
            className="grid grid-cols-2 sm:grid-cols-4 gap-2"
            data-testid="car-images-grid"
          >
            {slots.map((s) => (
              <Slot
                key={s.id}
                id={s.id}
                index={s.index}
                src={s.src}
                onSetMain={setMain}
                onRemove={remove}
                onPick={pickAt}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <div className="mt-2 flex items-center justify-between gap-3">
        <div className="text-[11px] text-white/40 leading-relaxed text-right">
          <ImageIcon size={11} className="inline text-primary/70 ml-1" />
          يتم ضغط الصور تلقائياً (Max 500KB / صورة) — اسحب الصور لإعادة الترتيب. الصورة الأولى هي الرئيسية.
        </div>
        {filled.length < MAX_IMAGES && (
          <button
            type="button"
            disabled={busy}
            onClick={() => pickAt(filled.length)}
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-primary/40 text-primary text-xs font-bold hover:bg-primary/10 disabled:opacity-60"
            data-testid="car-images-add-batch"
          >
            <UploadSimple size={12} weight="bold" />
            {busy ? "جاري الضغط…" : "إضافة صور"}
          </button>
        )}
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        multiple
        onChange={onFiles}
        className="hidden"
        data-testid="car-images-input"
      />
    </div>
  );
}
